import { Resolver, useForm } from 'react-hook-form';
import { LateFeeConfigProps } from '../types';
import { initialLateFeeConfigData } from './constant';
import { useCallback, useEffect, useState } from 'react';
import { SnackbarSeverityType } from '../../../enums';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import {
  getLateFeeConfigBySchool,
  upsertLateFeeConfig
} from '../../../apis/lateFeeConfig';
import { yupResolver } from '@hookform/resolvers/yup';
import { validateLateFeeConfigSchema } from './validationSchema';

export const useLateFeeConfig = () => {
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const {
    control,
    reset,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm<LateFeeConfigProps>({
    resolver: yupResolver(
      validateLateFeeConfigSchema
    ) as unknown as Resolver<LateFeeConfigProps>,
    mode: 'onChange',
    defaultValues: {
      ...initialLateFeeConfigData,
      school: loggedInUser.school
    }
  });
  watch();

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const [isLoading, setIsLoading] = useState(false);

  const fetchLateFeeConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getLateFeeConfigBySchool(loggedInUser.school);
      reset(response as any);
    } catch (error: any) {
      reset();
    } finally {
      setIsLoading(false);
    }
  }, [loggedInUser.school, reset]);

  useEffect(() => {
    fetchLateFeeConfig();
  }, [fetchLateFeeConfig]);

  const onError = useCallback((formErrors: typeof errors) => {
    const firstErrorKey = Object.keys(formErrors)[0];
    const firstErrorMessage = (formErrors as any)[firstErrorKey]?.message;
    setToastOpen(true);
    setToastSeverity(SnackbarSeverityType.error);
    setToastMessage(
      firstErrorMessage || `Please correct the form error in ${firstErrorKey}.`
    );
  }, []);

  const onSubmit = async (data: any) => {
    try {
      let updatedData = data;

      if (
        (data.fineType === 'PER_MONTH' && !data.proRatedConfig.enabled) ||
        data.fineType === 'PER_DAY'
      ) {
        updatedData = {
          ...data,
          proRatedConfig: null
        };
      }
      const response = await upsertLateFeeConfig(updatedData);
      if (response !== null && response !== undefined) {
        setToastOpen(true);
        setToastSeverity(SnackbarSeverityType.success);
        setToastMessage('Late-fee config updated succesfully');
      }
    } catch (error: any) {
      setToastOpen(true);
      setToastSeverity(SnackbarSeverityType.error);
      setToastMessage('error');
    }
  };

  return {
    control,
    isLoading,
    toastMessage,
    toastOpen,
    toastSeverity,
    handleSubmit: handleSubmit(onSubmit, onError),
    setToastOpen
  };
};
